import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('checking');
  const [paymentToken, setPaymentToken] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const registrationType = localStorage.getItem('registration_type') || 'user';

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const verifyPayment = async () => {
      try {
        const response = await axios.get(`${API}/payments/verify/${sessionId}`);
        
        if (response.data.status === 'paid' && response.data.payment_token) {
          setStatus('success');
          setPaymentToken(response.data.payment_token);
          // Store payment token for registration
          localStorage.setItem('payment_token', response.data.payment_token);
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    verifyPayment();
  }, [sessionId, attempts]);

  const handleContinue = () => {
    if (registrationType === 'professional') {
      navigate('/register-professional');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="payment-success-page">
      <Card className="w-full max-w-md border border-slate-200 rounded-md shadow-sm">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-orange-500 animate-spin mx-auto mb-6" />
              <h2 
                className="text-2xl font-semibold text-slate-900 mb-2"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {t('pay_checking')}
              </h2>
              <p className="text-slate-600">
                Verifica in corso... ({attempts + 1}/5)
              </p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 
                className="text-2xl font-semibold text-slate-900 mb-2"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {t('pay_success')}
              </h2>
              <p className="text-slate-600 mb-6">
                {registrationType === 'professional' 
                  ? 'Ora puoi completare la registrazione come professionista.'
                  : 'Ora puoi completare la registrazione.'}
              </p>
              <Button 
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm w-full"
                onClick={handleContinue}
                data-testid="continue-registration"
              >
                {t('auth_register_btn')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h2 
                className="text-2xl font-semibold text-slate-900 mb-2"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {t('common_error')}
              </h2>
              <p className="text-slate-600 mb-6">
                Si è verificato un errore durante la verifica del pagamento.
              </p>
              <Button 
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                onClick={() => navigate('/payment')}
                data-testid="retry-payment"
              >
                Riprova / Try again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
